import { useCallback, useEffect, useMemo } from 'react';
import { StateNodeType } from '@sierralabs/workflow';

import {
  useGetUserTaskSummary,
  useGetUserTrainingStats
} from '../../user/User.hooks';
import { useUserContext } from '../../user/User.context';
import { TaskSummaryItem } from '../tasks/Task.interface';
import { TaskTypeEntity } from '../task-types/TaskType.interface';
import { ProductTaskTypeEntities } from '../products/Product.interface';

const dashboardEntities: TaskTypeEntity[] = ProductTaskTypeEntities.concat(
  TaskTypeEntity.File
);

export function useDashboardPendingItems() {
  const { user } = useUserContext();
  const { data, get } = useGetUserTaskSummary();
  const { data: stats } = useGetUserTrainingStats(true, user?.id);

  const items: TaskSummaryItem[] = useMemo(() => data || [], [data]);

  const hasItems = useCallback(
    (...entities: TaskTypeEntity[]): boolean => {
      const total: number = items
        .filter((item: TaskSummaryItem) => entities.includes(item.targetEntity))
        .reduce((total: number, item: TaskSummaryItem) => {
          return total + item.total;
        }, 0);
      return total > 0;
    },
    [items]
  );

  const hasFileItems: boolean = useMemo(() => {
    return hasItems(TaskTypeEntity.File);
  }, [hasItems]);

  const hasProductItems: boolean = useMemo(() => {
    return hasItems(...ProductTaskTypeEntities);
  }, [hasItems]);

  const hasTrainingItems: boolean = useMemo(() => {
    const { toDoCount = 0, inProgressCount = 0 } = stats || {};
    return toDoCount + inProgressCount > 0;
  }, [stats]);

  const pendingItems: string[] = useMemo(() => {
    const items: string[] = [];
    hasFileItems && items.push('document');
    hasProductItems && items.push('product');
    hasTrainingItems && items.push('training');
    return items;
  }, [hasFileItems, hasProductItems, hasTrainingItems]);

  const hasPendingItems: boolean = useMemo(() => {
    return pendingItems.length > 0;
  }, [pendingItems]);

  // join items with comma and "and"
  const pendingItemsText: string = useMemo(() => {
    const i: string[] = pendingItems;
    const items: string[] =
      i.length < 3 ? i : [i.slice(0, -1).join(', ') + ',', ...i.slice(-1)];
    return items.join(' and ');
  }, [pendingItems]);

  useEffect(() => {
    get({
      entities: dashboardEntities,
      excludeTypes: [StateNodeType.Final]
    });
  }, [get]);

  return {
    hasFileItems,
    hasProductItems,
    hasTrainingItems,
    hasPendingItems,
    pendingItems,
    pendingItemsText
  };
}
